// 店舗を店舗グループ階層の下に並べるロジック

import { buildSellerGroupTree } from '@/lib/mise/sellerGroupTree';
import type { MiseSeller, MiseSellerGroupNode, MiseSellerNode } from '@/lib/types/mise/seller';

/**
 * 店舗プルダウンの1行。
 * group は見出し（選択不可）、seller は選択肢。配列順にそのまま描画すれば階層になる。
 */
export type SellerTreeOption =
  | { kind: 'group'; id: string; label: string; depth: number }
  | { kind: 'seller'; seller: MiseSeller; depth: number };

/** どのグループにも置けなかった店舗をまとめる見出しの名前 */
export const UNGROUPED_SELLERS_LABEL = 'グループ未所属';

function compareSellers(
  a: MiseSeller,
  b: MiseSeller,
  positions: Map<string, MiseSellerNode>,
): number {
  // sort_order が null のものは末尾に寄せ、同順位は名前順
  const orderA = positions.get(a.id)?.sort_order ?? Number.MAX_SAFE_INTEGER;
  const orderB = positions.get(b.id)?.sort_order ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return (a.name || a.id).localeCompare(b.name || b.id);
}

/**
 * 店舗を、所属する店舗グループの階層の下に並べたプルダウン行に変換する。
 *
 * - 店舗を含まないグループは出さない（祖先は店舗を含む限り見出しとして残す）。
 * - 所属グループが不明・階層に無い店舗は末尾の「グループ未所属」にまとめる。
 *
 * @param sellers 選択肢にする店舗（mise.sellers_current など）
 * @param groupNodes snapshot_hot 由来の店舗グループ階層
 * @param positions snapshot_hot 由来の店舗の所属グループ
 */
export function buildSellerTreeOptions(
  sellers: MiseSeller[],
  groupNodes: MiseSellerGroupNode[],
  positions: MiseSellerNode[],
): SellerTreeOption[] {
  const positionById = new Map(positions.map((p) => [p.id, p]));
  const tree = buildSellerGroupTree(groupNodes);
  const treeIds = new Set(tree.map((item) => item.node.id));
  const parentById = new Map(groupNodes.map((n) => [n.id, n.parent_id]));

  const sellersByGroup = new Map<string, MiseSeller[]>();
  const ungrouped: MiseSeller[] = [];
  for (const seller of sellers) {
    const parentId = positionById.get(seller.id)?.parent_id ?? null;
    if (parentId === null || !treeIds.has(parentId)) {
      ungrouped.push(seller);
      continue;
    }
    const list = sellersByGroup.get(parentId);
    if (list) {
      list.push(seller);
    } else {
      sellersByGroup.set(parentId, [seller]);
    }
  }

  // 店舗を持つグループとその祖先だけを見出しとして残す
  const visibleGroupIds = new Set<string>();
  for (const groupId of sellersByGroup.keys()) {
    let current: string | null | undefined = groupId;
    // 循環参照があっても止まるよう、既に見たグループで打ち切る
    while (current && !visibleGroupIds.has(current)) {
      visibleGroupIds.add(current);
      current = parentById.get(current);
    }
  }

  const options: SellerTreeOption[] = [];
  for (const item of tree) {
    if (!visibleGroupIds.has(item.node.id)) continue;
    options.push({
      kind: 'group',
      id: item.node.id,
      label: item.node.name || item.node.id,
      depth: item.depth,
    });
    const children = sellersByGroup.get(item.node.id) ?? [];
    children
      .sort((a, b) => compareSellers(a, b, positionById))
      .forEach((seller) => options.push({ kind: 'seller', seller, depth: item.depth + 1 }));
  }

  if (ungrouped.length > 0) {
    options.push({ kind: 'group', id: '', label: UNGROUPED_SELLERS_LABEL, depth: 0 });
    ungrouped
      .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
      .forEach((seller) => options.push({ kind: 'seller', seller, depth: 1 }));
  }

  return options;
}

/**
 * options[groupIndex] のグループの下にある店舗IDを（孫以下のグループ配下も含めて）返す。
 * options は pre-order で並ぶため、深さが見出し以下に戻るまでの行が配下になる。
 */
export function collectSellerIdsUnder(options: SellerTreeOption[], groupIndex: number): string[] {
  const group = options[groupIndex];
  if (!group || group.kind !== 'group') return [];
  const ids: string[] = [];
  for (let i = groupIndex + 1; i < options.length; i++) {
    const option = options[i];
    if (option.depth <= group.depth) break;
    if (option.kind === 'seller') ids.push(option.seller.id);
  }
  return ids;
}

/** ツリーの罫線（├─ / └─ / │）を描くための情報 */
export interface TreeConnector {
  /** 兄弟内で最後の行か（└─ / ├─ の出し分け用） */
  isLast: boolean;
  /** 各祖先が最後の子だったか（縦線 │ を引くかどうか、長さ = depth） */
  ancestorIsLast: boolean[];
}

/**
 * pre-order に並んだ行の深さの列から、罫線の描画情報を求める。
 * 店舗の行はグループツリーに含まれないため、行の並びそのものから兄弟関係を判断する。
 */
export function computeTreeConnectors(depths: number[]): TreeConnector[] {
  const isLastList = depths.map((depth, i) => {
    for (let j = i + 1; j < depths.length; j++) {
      if (depths[j] < depth) return true;
      if (depths[j] === depth) return false;
    }
    return true;
  });
  // 深さごとに直近の祖先が最後の子だったかを覚えておく
  const lastByDepth: boolean[] = [];
  return depths.map((depth, i) => {
    const ancestorIsLast = lastByDepth.slice(0, depth);
    // 最上位は枝記号を描かないので、末尾の「グループ未所属」と縦線でつながないようにする
    lastByDepth[depth] = depth === 0 ? true : isLastList[i];
    lastByDepth.length = depth + 1;
    return { isLast: isLastList[i], ancestorIsLast };
  });
}
